import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

export function Login() {
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const tenantSlugRef = useRef(tenantSlug);
  useEffect(() => { tenantSlugRef.current = tenantSlug; }, [tenantSlug]);
  const handleGoogleCallbackRef = useRef<(c: string, s?: string) => void>(() => {});
  useEffect(() => { handleGoogleCallbackRef.current = handleGoogleCallback; });

  useEffect(() => {
    // Process Google OAuth redirect: the id_token + slug were persisted to
    // sessionStorage BEFORE React mounted (main.tsx captureGoogleRedirect)
    // because the URL hash is lost when ProtectedLayout redirects to /login.
    const idToken = sessionStorage.getItem('googleIdToken');
    const error = sessionStorage.getItem('googleOAuthError');
    const slugFromOAuth = sessionStorage.getItem('googleOAuthSlug') || tenantSlugRef.current;

    if (error) {
      sessionStorage.removeItem('googleOAuthError');
      setError('Google rechazó el login: ' + error);
      return;
    }

    if (idToken) {
      sessionStorage.removeItem('googleIdToken');
      sessionStorage.removeItem('googleOAuthSlug');
      setTenantSlug(slugFromOAuth);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      handleGoogleCallbackRef.current(idToken, slugFromOAuth);
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-tenant-slug': tenantSlug },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Login failed' }));
        throw new Error(data.error || 'Login failed');
      }
      const { accessToken, refreshToken, user } = await res.json();
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('tenantSlug', tenantSlug);
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleCallback(credential: string, slugOverride?: string) {
    const effectiveSlug = slugOverride || tenantSlugRef.current;
    if (!effectiveSlug) {
      setError('Ingresa tu comercio (tenant) primero.');
      setGoogleLoading(false);
      return;
    }
    setGoogleLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, slug: effectiveSlug }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Google login failed' }));
        throw new Error(data.error || 'Google login failed');
      }
      const { accessToken, refreshToken, user, tenant } = await res.json();
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('tenantSlug', tenant.slug);
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google login failed');
    } finally {
      setGoogleLoading(false);
    }
  }

  function triggerGoogleLogin() {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google OAuth no configurado. Contactá al administrador.');
      return;
    }
    const slug = tenantSlug.trim();
    if (!slug) {
      setError('Ingresá tu comercio (tenant) primero.');
      return;
    }
    setGoogleLoading(true);
    setError('');

    // Redirect flow — más confiable que el popup del GSI (que suele ser
    // bloqueado), y compatible con el origin autorizado en Google Cloud.
    // Con response_type=id_token Google redirige a <origin>#id_token=...&state=...
    // y Login.tsx lo captura en el useEffect de montaje.
    const redirectUri = window.location.origin;
    const scope = 'openid email profile';
    const state = btoa(JSON.stringify({ slug }));
    const nonce = Math.random().toString(36).slice(2);
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=id_token&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(state)}&nonce=${nonce}&prompt=select_account`;
    window.location.href = authUrl;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-800">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold mb-6 text-center">Retail SaaS</h1>
        {error && <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Comercio</label>
            <input
              type="text"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              placeholder="mi-comercio"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Cargando...' : 'Ingresar'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-4">
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
          <span className="px-4 text-sm text-gray-400 dark:text-gray-400">o</span>
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
        </div>

        {/* Google Sign-In — botón propio con redirect flow (más confiable
            que el iframe/popup del GSI) */}
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <button
            type="button"
            onClick={triggerGoogleLogin}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {googleLoading ? 'Conectando con Google...' : 'Acceder con Google'}
          </button>
        </div>

        {/* Fallback button if GIS doesn't load */}
        {googleLoading && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Conectando con Google...</p>
        )}

        <p className="text-xs text-gray-400 dark:text-gray-400 text-center mt-4">
          Al continuar con Google, el usuario debe estar dado de alta
          previamente por el administrador del comercio.
        </p>
      </div>
    </div>
  );
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          renderButton: (container: HTMLElement, options: any) => void;
        };
      };
    };
  }
}